import type { UpsertStockPolicyPayload } from "@/modules/inventory/domain/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  inventoryStockPoliciesApi,
  type StockPoliciesListParams,
} from "./stock-policies-client";

export const stockPolicyKeys = {
  all: ["inventory", "stock-policies"] as const,
  list: (params?: StockPoliciesListParams) =>
    [...stockPolicyKeys.all, "list", params] as const,
  detail: (id: string, onHandBranchId?: string) =>
    [...stockPolicyKeys.all, "detail", id, onHandBranchId] as const,
};

export function useStockPoliciesQuery(params?: StockPoliciesListParams) {
  return useQuery({
    queryKey: stockPolicyKeys.list(params),
    queryFn: () => inventoryStockPoliciesApi.list(params),
  });
}

export function useUpsertStockPolicyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpsertStockPolicyPayload) =>
      inventoryStockPoliciesApi.upsert(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: stockPolicyKeys.all });
    },
  });
}

export function useBulkUpsertStockPoliciesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: UpsertStockPolicyPayload[]) =>
      inventoryStockPoliciesApi.bulkUpsert(items),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: stockPolicyKeys.all });
    },
  });
}

export function usePatchStockPolicyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: {
        minQty?: number;
        targetQty?: number | null;
        note?: string | null;
      };
    }) => inventoryStockPoliciesApi.patch(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: stockPolicyKeys.all });
    },
  });
}

export function useCopyGlobalPoliciesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      branchId: string;
      belowMinOnly?: boolean;
      itemType?: UpsertStockPolicyPayload["itemType"];
    }) => inventoryStockPoliciesApi.copyFromGlobal(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: stockPolicyKeys.all });
    },
  });
}

export function useImportStockPoliciesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (csv: string) => inventoryStockPoliciesApi.importCsv(csv),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: stockPolicyKeys.all });
    },
  });
}

export function useDeactivateStockPolicyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => inventoryStockPoliciesApi.deactivate(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: stockPolicyKeys.all });
    },
  });
}
